import {
  ArrowLeft,
  Check,
  CircleCheck,
  Info,
  Loader2,
  LogOut,
  Mic,
  MicOff,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  Video,
  VideoOff,
  Volume2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { BrandLockup } from '@/components/brand-mark';
import { SignalBars } from '@/components/signal-bars';
import { UserAvatar } from '@/components/user-avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

import { cn } from '@/lib/utils';
import {
  clearSsoFlow,
  clearSsoSession,
  completeSsoSignIn,
  browserSsoStorage,
  decodeAccessTokenPayload,
  loadSsoSession,
  parseSsoCallback,
  ssoConfigFromEnv,
  startSsoAutoRefresh,
  startSsoSignIn,
} from '../auth/sso';
import { loadStoredDevicePrefs, devicePrefsStore, type DevicePrefs } from './devicePrefs';
import { connectionQuality, measureControlRtt, qualitySignalLevel } from './connectionCheck';
import type { ConnectionQuality } from './connectionCheck';
import { DeviceSelect } from './DeviceSelect';
import { EffectsDialog } from './EffectsDialog';
import {
  startBackgroundProcessor,
  type BackgroundMode,
  type BackgroundOutcome,
  type BackgroundQualitySetting,
  type ResolvedBackgroundQuality,
} from '../media/backgroundProcessor';
import { effectsNeedPipeline } from '../media/videoEffects';
import type { BlurStrength, VideoFilter } from '../media/videoEffects';
import type { FaceMaskId } from '../media/faceMasks';
import { exposeCameraFxForTests } from './cameraEffects';
import { safePlay } from './media';
import { detectHardwareSignal, PUBLISH_TIER_CAPS, resolveSessionTier } from './publishQuality';
import type { PublishTier } from './publishQuality';
import {
  audioConstraintsOf,
  audioEffectSettingsFromPrefs,
  prefsPatchForEffect,
  readAudioEffectSettings,
  type AudioEffectKey,
  type AudioEffectSettings,
} from './micEffects';
import {
  enumerateInputDevices,
  listenDeviceChange,
  navigatorMediaDevices,
  openPreview,
  requestDevicePermission,
  stopPreview,
  type MediaDevicesLike,
  type PreviewStreams,
} from './previewMedia';
import {
  NO_DEVICE,
  deviceOptions,
  mediaMode,
  resolveSelection,
  roomLink,
  toJoinRequest,
  type InputDeviceInfoLike,
  type PrejoinJoinRequest,
} from './prejoinState';
import { CopyLinkButton } from './CopyLinkButton';
import {
  BackgroundModePicker,
  BackgroundQualityPicker,
  BlurStrengthPicker,
} from './BackgroundPickers';
import { checkDisplayName } from './roomState';
import type { DisplayNameError } from './roomState';
import { useMicLevel } from './useMicLevel';

interface PrejoinProps {
  readonly slug: string;
  /**
   * Ready-to-render failure notice from the room page (raw join failure detail, or the
   * already-translated display-name error) shown in the form's alert slot.
   */
  readonly joinError: string | null;
  /**
   * SSO notice surfaced by the room page: a failed SSO join (sso_invalid, sso_unavailable,
   * sso_disabled) or a mid-room session expiry. Non-blocking - the guest form stays usable.
   */
  readonly ssoNotice?: string | null | undefined;
  /** Reports an SSO notice to the room page; null clears the current one. */
  readonly onSsoNotice?: ((notice: string | null) => void) | undefined;
  /** Called with the validated payload when the participant presses Join. */
  readonly onJoin: (request: PrejoinJoinRequest) => void;
}

/** i18n keys of the participant-facing notice per degraded publish mode (camera or mic off). */
const MODE_NOTICE_KEYS = {
  'audio-only': 'prejoin.mode_notice_audio_only',
  'video-only': 'prejoin.mode_notice_video_only',
  'no-media': 'prejoin.mode_notice_no_media',
} as const;

/** Where the prejoin sign-in state sits. */
type SsoPhase = 'guest' | 'exchanging' | 'signed-in';

/**
 * Fallback display name for a token without preferred_username. Kept language-independent:
 * it is the value sent to the server for the SSO join payload (data, not UI copy); the UI
 * renders the translated sso.fallback_user variant where a name is displayed.
 */
const FALLBACK_USERNAME = 'SSO user';

/** Background treatments and quality levels: the pickers are shared with the effects dialog (BackgroundPickers.tsx). */
type BgMode = BackgroundMode;

/** Select value for the browser's default audio output (always available, even unencrypted). */
const DEFAULT_SPEAKER_ID = 'default';

/** Bars of the mockup's microphone meter. */
const METER_BARS = [0, 1, 2, 3, 4, 5, 6];

/**
 * The pre-join screen, styled 1:1 after the Syncra mockup: a full-height dark screen with the
 * brand top bar ("Waiting room" badge), a centered 880px card — camera preview with background
 * modes, status chips and quick toggles on the left, the info panel on the right (meeting
 * context, device checks with a real RTT probe, the live dB meter, device pickers, the mirror
 * option and the emerald Join CTA) — and the keyboard-hint footer
 * (D microphone, V camera, Enter join). The auth section sits in the panel: "Sign in with SSO"
 * starts the OIDC Authorization Code + PKCE flow against Keycloak, the callback lands back on
 * the room URL with an ?sso=callback marker (state-checked, exchanged in the browser, query
 * stripped), and the signed-in state joins with the server-verified ssoToken. Devices are
 * remembered between visits via the preference store (now including the speaker and the two
 * option toggles); so is the ENTRY DEVICE STATE: the mic/camera on-off state the participant
 * leaves the prejoin with is written back on every toggle and re-applied as the next join's
 * preselection (and publish decision) — the participant decides how they enter every call.
 * The screen degrades to audio-only / video-only when a device is missing or denied.
 * Functional logic (enumeration, preselect derivation, preview lifecycle, join payload)
 * keeps its unit coverage.
 */
export function Prejoin({
  slug,
  joinError,
  ssoNotice = null,
  onSsoNotice,
  onJoin,
}: PrejoinProps): JSX.Element {
  const { t } = useTranslation();
  const [media] = useState((): MediaDevicesLike | null => navigatorMediaDevices());
  const [devices, setDevices] = useState<readonly InputDeviceInfoLike[] | null>(null);
  const [cameraId, setCameraId] = useState(NO_DEVICE);
  const [micId, setMicId] = useState(NO_DEVICE);
  const [preview, setPreview] = useState<PreviewStreams | null>(null);
  const [nameError, setNameError] = useState<DisplayNameError | null>(null);
  const [ssoPhase, setSsoPhase] = useState<SsoPhase>('guest');
  const [ssoUsername, setSsoUsername] = useState<string | null>(null);
  // The mockup's device checks: pending spinner, then real values. `rttMs === undefined`
  // means the probe has not settled yet; null means the control service is unreachable.
  const [rttMs, setRttMs] = useState<number | null | undefined>(undefined);

  const prefs = useMemo((): DevicePrefs => loadStoredDevicePrefs(), []);
  const [bgMode, setBgMode] = useState<BgMode>((): BgMode => prefs.bgMode);
  const [bgQuality, setBgQuality] = useState<BackgroundQualitySetting>(
    (): BackgroundQualitySetting => prefs.bgQuality,
  );
  // The level the "auto" benchmark resolved to for the current processor run (null until the
  // benchmark settles or when a manual level runs); shown as "Auto · Eco/Max" in the selector.
  const [resolvedQuality, setResolvedQuality] = useState<ResolvedBackgroundQuality | null>(
    (): ResolvedBackgroundQuality | null => prefs.resolvedQuality,
  );
  // Video-effect choices: the blur strength (multiplier of the quality level's base blur
  // radius) and the whole-frame color filter. Both live-update the running processor and
  // persist through the join payload; the effects dialog exposes them on this surface too.
  const [blurStrength, setBlurStrength] = useState<BlurStrength>(
    (): BlurStrength => prefs.blurStrength,
  );
  const [videoFilter, setVideoFilter] = useState<VideoFilter>((): VideoFilter => prefs.videoFilter);
  // The face-mask overlay: live-updates the running processor (a first activation
  // lazily starts the local landmark engine) and persists through the join payload.
  const [faceMask, setFaceMask] = useState<FaceMaskId>((): FaceMaskId => prefs.mask);
  /**
   * The session publish tier for THIS prejoin visit (the room page resolves the same way at
   * join): sizes the preview capture constraints so the handed-over capture - and the
   * effects processor built on it - already publish at the session dimensions, and sizes
   * the processor's canvas cap (PUBLISH_TIER_CAPS). Derived, not frozen: an effects switch
   * (background, color filter, mask) re-derives it through resolveSessionTier BEFORE the
   * join, so an auto-'high' machine that starts with a treatment re-acquires the preview
   * and restarts the processor at the medium cap - the whole session then agrees on 720p
   * and no mid-call dimension change is ever needed (the stall lesson). A user-pinned
   * camQuality is never demoted.
   */
  const publishTier = useMemo(
    (): PublishTier =>
      resolveSessionTier(
        detectHardwareSignal(),
        prefs.camQuality,
        bgMode !== 'none' || videoFilter !== 'none' || faceMask !== 'none',
      ),
    [prefs.camQuality, bgMode, videoFilter, faceMask],
  );
  // Microphone processing switches (noise suppression, echo cancellation, auto gain): seeded
  // from the stored preferences; a dialog toggle updates this state (the preview re-acquires
  // the mic with the new constraints) and persists through the preference store so an
  // in-flight room join and the next visit both see it.
  const [micEffects, setMicEffects] = useState<AudioEffectSettings>((): AudioEffectSettings =>
    audioEffectSettingsFromPrefs(prefs),
  );
  const [effectsOpen, setEffectsOpen] = useState(false);
  // The processed preview stream when the background processor runs (null = raw camera).
  const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
  // One-time notice when backgrounds degraded to the raw camera track.
  const [bgNotice, setBgNotice] = useState<string | null>(null);
  const [speakerId, setSpeakerId] = useState(
    (): string => prefs.speakerDeviceId ?? DEFAULT_SPEAKER_ID,
  );
  const [displayName, setDisplayName] = useState((): string => prefs.displayName ?? '');
  const [mirrorVideo, setMirrorVideo] = useState((): boolean => prefs.mirrorVideo);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previewRef = useRef<PreviewStreams | null>(null);
  // The running background processor (null = none), the camera track and the quality level it
  // consumes, so a mode switch on the same camera+level upgrades the treatment instead of
  // restarting the engine (a quality change restarts: the canvas/fps/feather params differ).
  const processorRef = useRef<BackgroundOutcome | null>(null);
  const processorTrackRef = useRef<MediaStreamTrack | null>(null);
  const processorQualityRef = useRef<BackgroundQualitySetting | null>(null);
  // Set at submit when the processor is handed over to the room: the preview cleanup must
  // then keep the camera track alive (the room's published track derives from it).
  const handedOverRef = useRef(false);
  // Flips on the first selection resolution: afterwards a NO_DEVICE choice (user-picked or the
  // no-device fallback) is sticky across re-enumerations - a user who chose audio-only must not
  // get a camera resurrected by a devicechange pass.
  const selectionsResolvedRef = useRef(false);
  // Last non-empty selections: the quick toggles (and the D/V hotkeys) flip a kind off by
  // selecting NO_DEVICE and restore the previous device when toggled back on.
  const lastCameraIdRef = useRef<string>(NO_DEVICE);
  const lastMicIdRef = useRef<string>(NO_DEVICE);
  const cameraOptions = useMemo(() => deviceOptions(devices ?? [], 'videoinput'), [devices]);
  const micOptions = useMemo(() => deviceOptions(devices ?? [], 'audioinput'), [devices]);
  const speakerOptions = useMemo(
    () => speakerOptionsFrom(devices ?? [], t('prejoin.speakers_default')),
    [devices, t],
  );
  const ready = devices !== null;
  const checksSettled = ready && rttMs !== undefined;

  // Device enumeration (bootstrap + devicechange + "Check again"): passive first, then one
  // permission request per kind the browser hides behind permission. Extracted so the recheck
  // button re-runs the exact bootstrap path.
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async (): Promise<void> => {
      let list = await enumerateInputDevices(media);
      if (videoOptionsLength(list) === 0) {
        if (await requestDevicePermission('video', media)) {
          list = await enumerateInputDevices(media);
        }
      }
      if (audioOptionsLength(list) === 0) {
        if (await requestDevicePermission('audio', media)) {
          list = await enumerateInputDevices(media);
        }
      }
      if (!cancelled) {
        setDevices(list);
      }
    };
    void bootstrap();
    const unsubscribe = listenDeviceChange(() => {
      void (async () => {
        const list = await enumerateInputDevices(media);
        if (!cancelled) {
          setDevices(list);
        }
      })();
    }, media);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [media]);

  // The "Connection" check: a real RTT probe against the control service's /healthz. Re-runs
  // with the enumeration refresh via the check-run key ("Проверить снова").
  const [checkRun, setCheckRun] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setRttMs(undefined);
    void measureControlRtt().then((measured) => {
      if (!cancelled) {
        setRttMs(measured);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [checkRun]);

  /** Re-runs the device checks: enumeration (bootstrap path) + a fresh RTT probe. */
  function rerunChecks(): void {
    void (async () => {
      setDevices(await enumerateInputDevices(media));
    })();
    setCheckRun((run) => run + 1);
  }

  // Selections follow the device list: keep the current device while it exists, otherwise the
  // remembered one, otherwise the first available (preselect derivation, unit-tested). Only the
  // first pass after enumeration auto-resolves the initial empty selections; later passes keep
  // an explicit NO_DEVICE choice sticky (see selectionsResolvedRef).
  useEffect(() => {
    if (devices === null) {
      return;
    }
    const autoResolve = !selectionsResolvedRef.current;
    selectionsResolvedRef.current = true;
    setCameraId((current) => {
      const next = resolveSelection(cameraOptions, current, prefs.cameraDeviceId, autoResolve);
      if (next !== NO_DEVICE) {
        lastCameraIdRef.current = next;
      }
      // Remembered entry state: the camera starts off when the last join left it off. The
      // restore target stays in lastCameraIdRef, so the first toggle-on re-picks that device.
      if (autoResolve && !prefs.joinCamOn) {
        return NO_DEVICE;
      }
      return next;
    });
    setMicId((current) => {
      const next = resolveSelection(micOptions, current, prefs.micDeviceId, autoResolve);
      if (next !== NO_DEVICE) {
        lastMicIdRef.current = next;
      }
      if (autoResolve && !prefs.joinMicOn) {
        return NO_DEVICE;
      }
      return next;
    });
    setSpeakerId((current) =>
      resolveSelection(speakerOptions, current, prefs.speakerDeviceId, autoResolve),
    );
  }, [cameraOptions, micOptions, speakerOptions, devices, prefs]);

  // Local preview for the selected devices; stopped and reopened whenever a selection changes.
  // A failing device degrades to a null stream (placeholder + meter stays at 0), never blocks.
  useEffect(() => {
    if (devices === null) {
      return;
    }
    let cancelled = false;
    let opened: PreviewStreams | null = null;
    void (async () => {
      const streams = await openPreview(
        cameraId === NO_DEVICE ? null : cameraId,
        micId === NO_DEVICE ? null : micId,
        media,
        // Microphone processing switches ride the acquisition constraints: the preview
        // (and its meter) reflect exactly what the effects dialog asked for.
        audioConstraintsOf(micEffects),
        // The session tier's resolution: the handed-over capture must arrive at the
        // session dimensions the room publishes at (the join reuses this capture when
        // the background processor is on).
        {
          width: PUBLISH_TIER_CAPS[publishTier].width,
          height: PUBLISH_TIER_CAPS[publishTier].height,
          frameRate: 30,
        },
      );
      if (cancelled) {
        stopPreview(streams);
        return;
      }
      opened = streams;
      previewRef.current = streams;
      setPreview(streams);
      if (streams.video !== null || streams.mic !== null) {
        // Permission was just granted: labels (and real device ids) become visible now.
        const refreshed = await enumerateInputDevices(media);
        if (!cancelled) {
          setDevices(refreshed);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (opened !== null) {
        if (handedOverRef.current) {
          // Join handed the processor (and its input camera track) to the room: keep the
          // camera alive, stop only the microphone.
          stopPreview({ video: null, mic: opened.mic });
        } else {
          stopPreview(opened);
        }
        if (previewRef.current === opened) {
          previewRef.current = null;
          setPreview(null);
        }
      }
    };
    // `micEffects` rides the deps: a switch toggle re-opens the preview with the new
    // processing constraints (the documented ~1-2 s microphone restart).
  }, [cameraId, micId, devices === null, media, micEffects, publishTier]);

  // Background processor lifecycle: runs whenever the composite pipeline is needed - a
  // background treatment (blur/office), a whole-frame color filter OR a face mask (the
  // raw+filter and raw+mask combinations route through the composite; the documented
  // cost is the composite pass, segmentation skipped) - and stops when everything is
  // off or the camera is off. A treatment/strength/filter/mask change on the same camera
  // and level live-updates the running processor (setMode/setBlurStrength/setFilter/
  // setMask); a quality change restarts it (the benchmark reruns for a fresh "auto").
  // A start/segmentation failure degrades to the raw camera with a one-time notice.
  useEffect(() => {
    const cameraStream = preview?.video ?? null;
    const cameraTrack = cameraStream?.getVideoTracks()[0] ?? null;
    const resetProcessor = (): void => {
      processorRef.current = null;
      processorTrackRef.current = null;
      processorQualityRef.current = null;
      setProcessedStream(null);
    };
    if (!effectsNeedPipeline(bgMode, videoFilter, faceMask) || cameraTrack === null) {
      processorRef.current?.stop();
      resetProcessor();
      return undefined;
    }
    // A running processor for the same camera and level just switches treatment and
    // strength/filter/mask live; anything else (different camera or a quality change)
    // stops the old processor before a new starts.
    if (processorRef.current !== null) {
      if (processorTrackRef.current === cameraTrack && processorQualityRef.current === bgQuality) {
        processorRef.current.setMode(bgMode);
        if (processorRef.current.processed) {
          processorRef.current.setBlurStrength(blurStrength);
          processorRef.current.setFilter(videoFilter);
          processorRef.current.setMask(faceMask);
        }
        return undefined;
      }
      processorRef.current.stop();
      resetProcessor();
    }
    let cancelled = false;
    void (async () => {
      const outcome = await startBackgroundProcessor(cameraTrack, bgMode, {
        quality: bgQuality,
        // The session tier's cap: the canvas publishes at the size the raw capture
        // publishes at, so the in-call raw<->canvas swap never changes dimensions.
        publishCap: PUBLISH_TIER_CAPS[publishTier],
        blurStrength,
        filter: videoFilter,
        mask: faceMask,
        onFallback: () => {
          if (!cancelled) {
            resetProcessor();
            setResolvedQuality(null);
            setBgNotice(t('prejoin.bg_unavailable'));
          }
        },
      });
      if (cancelled) {
        outcome.stop();
        return;
      }
      if (!outcome.processed) {
        resetProcessor();
        setResolvedQuality(null);
        setBgNotice(
          outcome.reason === 'too_slow'
            ? t('prejoin.bg_quality_too_slow')
            : t('prejoin.bg_unavailable'),
        );
        return;
      }
      processorRef.current = outcome;
      processorTrackRef.current = cameraTrack;
      processorQualityRef.current = bgQuality;
      setResolvedQuality(outcome.resolvedQuality);
      setProcessedStream(outcome.stream);
    })();
    return () => {
      cancelled = true;
    };
  }, [preview?.video, bgMode, bgQuality, blurStrength, videoFilter, faceMask, publishTier, t]);

  // Stops and forgets the processor (unless it was handed over to the room on join).
  function stopBackgroundProcessor(): void {
    processorRef.current?.stop();
    processorRef.current = null;
    processorTrackRef.current = null;
    processorQualityRef.current = null;
  }

  // Dev/e2e bridge: the preview pipeline's live effect state for page evaluation
  // (production builds strip it, like every window bridge).
  useEffect(() => {
    exposeCameraFxForTests(() => {
      const outcome = processorRef.current;
      if (outcome === null || !outcome.processed) {
        return null;
      }
      return {
        request: {
          mode: bgMode,
          filter: videoFilter,
          strength: blurStrength,
          quality: bgQuality,
          mask: faceMask,
        },
        processed: true,
      };
    });
  }, [bgMode, blurStrength, videoFilter, bgQuality, faceMask, processedStream]);

  // Attach the preview stream to the (muted) video element: the processed canvas stream
  // when the background processor runs, the raw camera otherwise.
  useEffect(() => {
    const element = videoRef.current;
    if (element === null) {
      return;
    }
    const stream = processedStream ?? preview?.video ?? null;
    element.srcObject = stream;
    if (stream != null) {
      safePlay(element);
    }
    return () => {
      element.srcObject = null;
    };
  }, [preview, processedStream]);

  // Leaves the prejoin without joining (navigation, hot remount): release the processor.
  useEffect(
    () => () => {
      stopBackgroundProcessor();
    },
    [],
  );

  // SSO: consume an ?sso=callback landing (exactly once per page load - the StrictMode remount
  // re-runs this effect, hence the ref guard) or restore an existing tab session. The query is
  // stripped before the exchange so a reload can never replay the one-time code.
  const ssoConsumedRef = useRef(false);
  useEffect(() => {
    if (ssoConsumedRef.current) {
      return;
    }
    ssoConsumedRef.current = true;
    const storage = browserSsoStorage();
    const callback = parseSsoCallback(window.location.search);
    if (callback.kind === 'none') {
      const session = storage !== null ? loadSsoSession(storage) : null;
      if (session !== null) {
        setSsoUsername(usernameForToken(session.accessToken));
        setSsoPhase('signed-in');
      }
      return;
    }
    window.history.replaceState({}, '', window.location.pathname);
    if (callback.kind === 'provider_error') {
      // The flow is done (and rejected): drop its state so no stale verifier lingers.
      if (storage !== null) {
        clearSsoFlow(storage);
      }
      onSsoNotice?.(
        t('sso.notice_provider_error', { detail: callback.description ?? callback.error ?? '' }),
      );
      return;
    }
    if (storage === null) {
      onSsoNotice?.(t('sso.notice_storage_blocked'));
      return;
    }
    setSsoPhase('exchanging');
    void completeSsoSignIn(
      ssoConfigFromEnv(),
      { code: callback.code, state: callback.state },
      { room: slug, origin: window.location.origin },
      storage,
    ).then((outcome) => {
      switch (outcome.kind) {
        case 'signed_in':
          setSsoUsername(outcome.username);
          setSsoPhase('signed-in');
          onSsoNotice?.(null);
          return;
        case 'stale_state':
          setSsoPhase('guest');
          onSsoNotice?.(t('sso.notice_stale_state'));
          return;
        case 'provider_error':
        case 'exchange_failed':
          setSsoPhase('guest');
          onSsoNotice?.(t('sso.notice_exchange_failed', { detail: outcome.message }));
          return;
      }
    });
  }, [slug, onSsoNotice]);

  // Auto-refresh: a signed-in visitor can stay on the prejoin as long as they like; the
  // scheduler rotates the token pair near expiry so Join never posts a stale token (and the
  // joinWithSsoRecovery wrapper on the room page covers the remaining race).
  useEffect(() => {
    const storage = browserSsoStorage();
    if (storage === null) {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: ssoConfigFromEnv(),
      storage,
    });
  }, []);

  const mic = useMicLevel(preview?.mic ?? null);
  // Effective processing state of the preview mic (getSettings): drives the dialog's live
  // badges when the browser reports it; null falls back to the stored preference display.
  const effectiveMicEffects = readAudioEffectSettings(preview?.mic?.getAudioTracks()[0] ?? null);
  const link = roomLink(window.location.origin, slug);
  const signedIn = ssoPhase === 'signed-in';
  const mode = mediaMode(cameraId, micId);
  const quality = connectionQuality(rttMs ?? null);

  /**
   * Applies one microphone selection and remembers its on/off state for the next join
   * (write-on-toggle: the participant decides the entry state, every change persists at once).
   */
  function selectMic(deviceId: string): void {
    setMicId(deviceId);
    devicePrefsStore().update({ joinMicOn: deviceId !== NO_DEVICE });
  }

  /** Applies one camera selection and remembers its on/off state for the next join. */
  function selectCamera(deviceId: string): void {
    setCameraId(deviceId);
    devicePrefsStore().update({ joinCamOn: deviceId !== NO_DEVICE });
  }

  /** Flips the microphone kind off/on (quick toggle + D hotkey); the state is remembered. */
  function toggleMic(): void {
    if (micId === NO_DEVICE) {
      selectMic(lastMicIdRef.current);
      return;
    }
    lastMicIdRef.current = micId;
    selectMic(NO_DEVICE);
  }

  /** Flips the camera kind off/on (quick toggle + V hotkey); the state is remembered. */
  function toggleCamera(): void {
    if (cameraId === NO_DEVICE) {
      selectCamera(lastCameraIdRef.current);
      return;
    }
    lastCameraIdRef.current = cameraId;
    selectCamera(NO_DEVICE);
  }

  /**
   * One effects-dialog microphone switch: updates the local state (the preview re-acquires
   * the mic with the new constraints) and persists through the preference store.
   */
  function handleEffectToggle(key: AudioEffectKey, value: boolean): void {
    setMicEffects((current) => ({ ...current, [key]: value }));
    devicePrefsStore().update(prefsPatchForEffect(key, value));
  }

  /** Starts the redirect-based OIDC sign-in; the flow state rides in sessionStorage. */
  function handleSsoSignIn(): void {
    onSsoNotice?.(null);
    const storage = browserSsoStorage();
    if (storage === null) {
      onSsoNotice?.(t('sso.notice_storage_unavailable'));
      return;
    }
    void startSsoSignIn(
      ssoConfigFromEnv(),
      { room: slug, origin: window.location.origin },
      storage,
    );
  }

  /** Clears the tab's sign-in session and returns to the guest form. */
  function handleSsoSignOut(): void {
    const storage = browserSsoStorage();
    if (storage !== null) {
      clearSsoSession(storage);
    }
    setSsoUsername(null);
    setSsoPhase('guest');
    onSsoNotice?.(null);
  }

  /**
   * Builds the background handover for the join payload: present whenever the composite
   * pipeline actually runs and its output is live - a background treatment or a whole-frame
   * color filter (the raw+filter combination publishes the composite too). Claiming
   * ownership makes the prejoin's own cleanup a no-op - the room page disposes the
   * processor (and its input camera) instead, and its effects machine rebuilds future
   * pipeline generations from the handover's raw capture.
   */
  function backgroundHandover(): PrejoinJoinRequest['background'] {
    const processor = processorRef.current;
    if (processor === null || !processor.processed) {
      return undefined;
    }
    if (processor.track.readyState !== 'live') {
      return undefined;
    }
    processor.claimOwnership();
    handedOverRef.current = true;
    return {
      mode: bgMode,
      track: processor.track,
      cameraTrack: processor.cameraTrack,
      dispose: () => {
        processor.dispose();
      },
    };
  }

  function handleSubmit(): void {
    onSsoNotice?.(null);
    const storage = browserSsoStorage();
    const session = signedIn && storage !== null ? loadSsoSession(storage) : null;
    if (signedIn && session !== null) {
      // SSO join: the display name comes from the verified token server-side; the username is
      // display-only. The server identity (kc-<sub>) is authoritative and echoed in the response.
      onJoin({
        displayName: ssoUsername ?? FALLBACK_USERNAME,
        cameraDeviceId: cameraId === NO_DEVICE ? null : cameraId,
        micDeviceId: micId === NO_DEVICE ? null : micId,
        ssoToken: session.accessToken,
        speakerDeviceId: speakerId,
        mirrorVideo,
        backgroundMode: bgMode,
        backgroundQuality: bgQuality,
        backgroundBlurStrength: blurStrength,
        backgroundVideoFilter: videoFilter,
        backgroundMask: faceMask,
        backgroundResolvedQuality: resolvedQuality,
        background: backgroundHandover(),
        micEchoCancellation: micEffects.echoCancellation,
        micNoiseSuppression: micEffects.noiseSuppression,
        micAutoGainControl: micEffects.autoGainControl,
      });
      return;
    }
    if (signedIn) {
      // The session vanished from storage: drop back to the guest form instead of failing.
      setSsoPhase('guest');
      setSsoUsername(null);
    }
    const check = checkDisplayName(displayName);
    if (check.error !== null) {
      setNameError(check.error);
      return;
    }
    setNameError(null);
    onJoin({
      ...toJoinRequest(check.value, cameraId, micId),
      speakerDeviceId: speakerId,
      mirrorVideo,
      backgroundMode: bgMode,
      backgroundQuality: bgQuality,
      backgroundBlurStrength: blurStrength,
      backgroundVideoFilter: videoFilter,
      backgroundMask: faceMask,
      backgroundResolvedQuality: resolvedQuality,
      micEchoCancellation: micEffects.echoCancellation,
      micNoiseSuppression: micEffects.noiseSuppression,
      micAutoGainControl: micEffects.autoGainControl,
      // Claimed only after validation passes, so a failed submit keeps the processor.
      background: backgroundHandover(),
    });
  }

  // Real hotkeys, exactly as the mockup: D — microphone, V — camera, Enter — join. Typing
  // targets (inputs, selects, editable elements) are exempt; modifier chords are ignored.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === 'd' || event.code === 'KeyD') {
        event.preventDefault();
        toggleMic();
      } else if (key === 'v' || event.code === 'KeyV') {
        event.preventDefault();
        toggleCamera();
      } else if (key === 'enter') {
        event.preventDefault();
        if (ready) {
          handleSubmit();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  });

  // The form's alert slot: a local display-name validation error (translated here) wins over
  // the ready-to-render notice handed down from the room page.
  const alert = nameError !== null ? t(`prejoin.name_error_${nameError}`) : joinError;
  const hasCameraPreview = preview?.video != null;
  const avatarName =
    ssoUsername ?? (displayName.trim().length > 0 ? displayName : t('prejoin.avatar_fallback'));
  const badgeName =
    ssoUsername ??
    (displayName.trim().length > 0 ? displayName.trim() : t('prejoin.avatar_fallback'));
  const camOn = cameraId !== NO_DEVICE;
  const micOn = micId !== NO_DEVICE;

  return (
    <section
      aria-label={t('prejoin.section_aria')}
      className="relative flex w-full flex-col text-zinc-100"
    >
      {/* background glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/3 size-[560px] -translate-x-1/2 rounded-full bg-emerald-500/[0.07] blur-3xl"
      />

      {/* top bar */}
      <div className="relative z-10 flex h-12 shrink-0 items-center justify-between px-1">
        <BrandLockup />
        <Badge variant="outline" className="border-white/10 text-[11px] text-zinc-500">
          {t('prejoin.badge')}
        </Badge>
      </div>

      {/* body */}
      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center p-1">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="flex w-full max-w-[880px] flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-zinc-900/60 shadow-2xl backdrop-blur-sm lg:flex-row"
        >
          {/* preview */}
          <div
            data-testid="camera-preview"
            data-bg={bgMode}
            data-mask={faceMask}
            data-cam={camOn ? 'on' : 'off'}
            data-mic={micOn ? 'on' : 'off'}
            className="relative aspect-video w-full shrink-0 overflow-hidden rounded-xl ring-1 ring-white/[0.06] lg:aspect-auto lg:w-[55%] lg:rounded-none lg:ring-0"
          >
            {hasCameraPreview ? (
              <>
                {/*
                 * The video shows the PROCESSED canvas stream when a background treatment is
                 * active (segmentation keeps the person sharp; the background is blurred or
                 * the generated office scene) and the raw camera otherwise. The same processed
                 * track is handed to the room and published on join.
                 */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={cn(
                    'absolute inset-0 h-full w-full object-cover',
                    mirrorVideo && '-scale-x-100',
                  )}
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.5)_100%)]"
                />
              </>
            ) : (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center text-sm text-zinc-500"
                style={{ background: '#0d0d0f' }}
              >
                {!ready ? (
                  <>
                    <Loader2 aria-hidden className="h-6 w-6 animate-spin text-zinc-600" />
                    <span>{t('prejoin.looking_for_devices')}</span>
                  </>
                ) : !camOn ? (
                  <>
                    <UserAvatar name={avatarName} size="xl" />
                    <span>{t('prejoin.camera_off')}</span>
                  </>
                ) : (
                  <span>{t('prejoin.camera_preview_unavailable')}</span>
                )}
              </div>
            )}

            {/* device-check status chip */}
            {!checksSettled ? (
              <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-lg bg-black/55 px-2.5 py-1.5 text-[11.5px] font-medium text-zinc-300 backdrop-blur-sm">
                <span className="size-1.5 animate-pulse rounded-full bg-amber-400" />
                {t('prejoin.checking_devices')}
              </div>
            ) : (
              <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-lg bg-black/55 px-2.5 py-1.5 text-[11.5px] font-medium text-emerald-400 backdrop-blur-sm">
                <CircleCheck className="size-3.5" />
                {t('prejoin.ready_to_join')}
              </div>
            )}

            {/* connection quality chip */}
            <div className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-lg bg-black/55 px-2 py-1 text-[10.5px] tabular-nums text-zinc-300 backdrop-blur-sm">
              <SignalBars level={qualitySignalLevel(quality)} />
              {t('prejoin.quality_chip')}
            </div>

            {/* name badge */}
            <div className="absolute bottom-12 left-3 inline-flex items-center rounded-lg bg-black/55 px-2.5 py-1.5 text-[11.5px] font-medium text-white backdrop-blur-sm">
              {badgeName} · {t('prejoin.preview_you')}
            </div>

            {/* background modes (on-device segmentation: sharp person, blurred/replaced
                background), the quality selector and the blur strength row (strength only
                while blur is active); the pickers are shared with the effects dialog, only
                the testid prefixes differ (e2e pins). */}
            {ready ? (
              <div className="absolute bottom-3 left-3 flex flex-col items-start gap-1.5">
                <BackgroundModePicker mode={bgMode} onMode={setBgMode} testidPrefix="bg-mode" />
                {bgMode !== 'none' ? (
                  <BackgroundQualityPicker
                    quality={bgQuality}
                    onQuality={setBgQuality}
                    resolvedQuality={resolvedQuality}
                    testidPrefix="bg-quality"
                    wrapperTestid="bg-quality"
                  />
                ) : null}
                {bgMode === 'blur' ? (
                  <BlurStrengthPicker
                    strength={blurStrength}
                    onStrength={setBlurStrength}
                    testidPrefix="blur-strength"
                    wrapperTestid="blur-strength"
                  />
                ) : null}
              </div>
            ) : null}

            {/* quick toggles on the preview */}
            <div className="absolute bottom-3 right-3 flex gap-1.5">
              <button
                type="button"
                aria-label={micOn ? t('controls.mute_mic') : t('controls.unmute_mic')}
                onClick={toggleMic}
                className={cn(
                  'flex size-10 items-center justify-center rounded-xl shadow-lg transition-colors',
                  micOn
                    ? 'bg-white/15 text-white backdrop-blur-sm hover:bg-white/25'
                    : 'bg-red-500 text-white',
                )}
              >
                {micOn ? <Mic className="size-4" /> : <MicOff className="size-4" />}
              </button>
              <button
                type="button"
                aria-label={camOn ? t('controls.cam_off') : t('controls.cam_on')}
                onClick={toggleCamera}
                className={cn(
                  'flex size-10 items-center justify-center rounded-xl shadow-lg transition-colors',
                  camOn
                    ? 'bg-white/15 text-white backdrop-blur-sm hover:bg-white/25'
                    : 'bg-red-500 text-white',
                )}
              >
                {camOn ? <Video className="size-4" /> : <VideoOff className="size-4" />}
              </button>
            </div>
          </div>

          {/* info panel */}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
            className="flex min-w-0 flex-1 flex-col p-5 lg:p-6"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
              {t('prejoin.room_now')}
            </p>
            <h1 className="mt-2 truncate text-[19px] font-semibold leading-snug tracking-tight text-white">
              <span className="font-mono">{slug}</span>
            </h1>

            {/* identity line: SSO chip when signed in, nothing for guests (no organizer data) */}
            {signedIn ? (
              <div
                data-testid="sso-signed-in"
                className="mt-3 flex items-center gap-2.5 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.07] px-3 py-2"
              >
                <UserAvatar name={ssoUsername ?? t('sso.fallback_user')} size="sm" />
                <span className="min-w-0 flex-1 truncate text-xs text-zinc-400">
                  {t('sso.signed_in_as', { name: ssoUsername ?? t('sso.fallback_user') })}
                </span>
                <button
                  type="button"
                  data-testid="sso-signout"
                  aria-label={t('sso.sign_out')}
                  title={t('sso.sign_out')}
                  onClick={handleSsoSignOut}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-300"
                >
                  <LogOut aria-hidden className="size-3.5" />
                </button>
              </div>
            ) : ssoPhase === 'exchanging' ? (
              <p
                role="status"
                className="mt-3 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-zinc-300"
              >
                <Loader2 aria-hidden className="size-3.5 animate-spin" />
                {t('sso.finishing')}
              </p>
            ) : null}

            {/* guest display name (mockup-input styling; sr-only label keeps the accessible name) */}
            {!signedIn ? (
              <label htmlFor="display-name" className="mt-3 flex flex-col gap-1">
                <span className="sr-only">{t('prejoin.display_name')}</span>
                <input
                  id="display-name"
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                  }}
                  maxLength={64}
                  placeholder={t('prejoin.name_placeholder')}
                  autoComplete="off"
                  className="h-9 rounded-lg border border-white/10 bg-white/[0.04] px-3 text-xs text-zinc-200 outline-none placeholder:text-zinc-600 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                />
              </label>
            ) : null}
            {!signedIn && ssoPhase === 'guest' ? (
              <div className="mt-2 flex justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="sso-signin"
                  onClick={handleSsoSignIn}
                  className="h-7 gap-1.5 rounded-lg px-2 text-[11px] text-zinc-500 hover:bg-white/5 hover:text-zinc-300"
                >
                  {t('sso.sign_in')}
                </Button>
              </div>
            ) : null}

            {/* device checks */}
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                  {t('prejoin.checks_title')}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={rerunChecks}
                  className="h-7 gap-1.5 rounded-lg px-2 text-[11px] text-zinc-500 hover:bg-white/5 hover:text-zinc-300"
                >
                  <RotateCcw className="size-3.5" />
                  {t('prejoin.recheck')}
                </Button>
              </div>
              <div data-testid="prejoin-checks" className="mt-2 space-y-2">
                <CheckRow
                  testid="check-connection"
                  label={t('prejoin.check_connection')}
                  pending={rttMs === undefined}
                  value={connectionCheckValue(rttMs, quality, t)}
                />
                <CheckRow
                  testid="check-camera"
                  label={t('prejoin.check_camera')}
                  pending={!ready}
                  value={
                    !ready
                      ? '…'
                      : (cameraOptions.find((option) => option.deviceId === cameraId)?.label ??
                        t('prejoin.check_missing'))
                  }
                />
                <CheckRow
                  testid="check-mic"
                  label={t('prejoin.check_mic')}
                  pending={!ready}
                  value={
                    !ready
                      ? '…'
                      : (micOptions.find((option) => option.deviceId === micId)?.label ??
                        t('prejoin.check_missing'))
                  }
                />

                {/* live microphone meter */}
                <div className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-2">
                  {micOn ? (
                    <Mic className="size-3.5 shrink-0 text-emerald-400" />
                  ) : (
                    <MicOff className="size-3.5 shrink-0 text-red-400" />
                  )}
                  <div
                    data-testid="mic-level"
                    role="meter"
                    aria-label={t('prejoin.mic_level_aria')}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(mic.level * 100)}
                    className="flex h-4 min-w-0 flex-1 items-end gap-[3px]"
                  >
                    {METER_BARS.map((bar) => {
                      const lit = mic.level * METER_BARS.length - bar;
                      const height = Math.max(0, Math.min(1, lit)) * 100;
                      return (
                        <span
                          key={bar}
                          className="relative h-full w-1 overflow-hidden rounded-full bg-white/[0.06]"
                        >
                          <span
                            className={cn(
                              'absolute bottom-0 left-0 w-full rounded-full transition-[height] duration-150 ease-out',
                              micOn ? 'bg-emerald-400' : 'bg-zinc-700',
                            )}
                            style={{ height: `${height}%` }}
                          />
                        </span>
                      );
                    })}
                  </div>
                  {micOn ? (
                    <span
                      data-testid="meter-db"
                      className="ml-auto text-[11px] tabular-nums text-zinc-400"
                    >
                      {t('prejoin.meter_db_value', { value: mic.db })}
                    </span>
                  ) : (
                    <span className="ml-auto text-[11px] text-zinc-500">
                      {t('prejoin.meter_muted')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* device selects */}
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <DeviceSelect
                id="camera-select"
                label={t('prejoin.camera_label')}
                icon={Video}
                value={cameraId}
                options={cameraOptions}
                noDeviceLabel={t('prejoin.no_camera')}
                testid="camera-select"
                onChange={selectCamera}
              />
              <DeviceSelect
                id="mic-select"
                label={t('prejoin.mic_label')}
                icon={Mic}
                value={micId}
                options={micOptions}
                noDeviceLabel={t('prejoin.no_microphone')}
                testid="mic-select"
                onChange={selectMic}
              />
              <DeviceSelect
                id="speakers-select"
                label={t('prejoin.speakers_label')}
                icon={Volume2}
                value={speakerId}
                options={speakerOptions}
                testid="speakers-select"
                onChange={setSpeakerId}
              />
            </div>

            {/* options */}
            <div className="mt-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] text-zinc-300">{t('prejoin.opt_mirror')}</span>
                <Switch
                  data-testid="opt-mirror"
                  checked={mirrorVideo}
                  onCheckedChange={setMirrorVideo}
                  className="scale-90 data-[state=checked]:bg-emerald-500"
                />
              </div>
              {/* The effects dialog entry (microphone processing + camera background); the
                  same dialog opens from the in-call control bar's «Ещё» menu. */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="effects-button"
                onClick={() => {
                  setEffectsOpen(true);
                }}
                className="h-8 w-full justify-start gap-2 rounded-lg px-2 text-[12px] text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
              >
                <SlidersHorizontal className="size-3.5" />
                {t('controls.effects')}
              </Button>
              {/* The entry mic/camera state is remembered: whatever the toggles read here is
                  the next join's preselection and publish decision. */}
              <p
                data-testid="join-memory-hint"
                className="flex items-center justify-center gap-1.5 text-[10.5px] text-zinc-600"
              >
                <Info aria-hidden className="size-3" />
                {t('prejoin.options_memory_hint')}
              </p>
            </div>

            {ready && mode !== 'audio-video' ? (
              <p
                role="status"
                data-testid="media-mode-notice"
                className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              >
                {t(MODE_NOTICE_KEYS[mode])}
              </p>
            ) : null}

            {bgNotice !== null ? (
              <p
                role="status"
                data-testid="bg-notice"
                className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              >
                {bgNotice}
              </p>
            ) : null}

            {ssoNotice !== null ? (
              <p
                role="status"
                data-testid="sso-notice"
                className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              >
                {ssoNotice}
              </p>
            ) : null}

            {alert !== null ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
              >
                {alert}
              </p>
            ) : null}

            <div className="mt-auto pt-5">
              <motion.div
                className="w-full"
                whileTap={{ scale: 0.98 }}
                {...(ready ? { whileHover: { scale: 1.01 } as const } : {})}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                <Button
                  type="submit"
                  data-testid="join-cta"
                  className={cn(
                    'h-11 w-full gap-2 rounded-xl text-sm font-semibold transition-all',
                    ready
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-800',
                  )}
                >
                  {ready ? (
                    <Check className="size-4" />
                  ) : (
                    <Sparkles aria-hidden className="size-4 animate-pulse" />
                  )}
                  {ready ? t('prejoin.join_cta') : t('prejoin.connecting')}
                </Button>
              </motion.div>
              <div className="mt-3 flex items-center justify-center gap-2">
                <a
                  href="/"
                  className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-xs text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-300"
                >
                  <ArrowLeft aria-hidden className="size-3.5" />
                  {t('prejoin.back')}
                </a>
                <CopyLinkButton url={link} compact />
                {/* The shareable room link, kept for e2e/tooling; never contains a token. */}
                <input
                  data-testid="room-url"
                  aria-label={t('prejoin.room_link_aria')}
                  readOnly
                  tabIndex={-1}
                  value={link}
                  className="sr-only"
                />
              </div>
              <p className="mt-2 flex items-center justify-center gap-1.5 text-[10.5px] text-zinc-600">
                <Info aria-hidden className="size-3" />
                {t('prejoin.lobby_note')}
              </p>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Effects dialog: microphone processing switches (live on the preview mic), the
          whole-frame color filters and the shared camera pickers - all live on the preview
          pipeline (the same choices drive the in-call machine through the store). */}
      <EffectsDialog
        open={effectsOpen}
        onOpenChange={setEffectsOpen}
        camera={{
          bgMode,
          bgQuality,
          resolvedQuality,
          blurStrength,
          onBgMode: setBgMode,
          onBgQuality: setBgQuality,
          onBlurStrength: setBlurStrength,
        }}
        filters={{
          filter: videoFilter,
          onFilter: setVideoFilter,
        }}
        masks={{
          mask: faceMask,
          onMask: setFaceMask,
        }}
        microphone={{
          settings: micEffects,
          effective: effectiveMicEffects,
          busy: false,
          onToggle: handleEffectToggle,
        }}
      />

      {/* footer keyboard hints */}
      <div
        data-testid="prejoin-hints"
        className="relative z-10 flex shrink-0 items-center justify-center gap-4 pb-4 text-[10.5px] text-zinc-600"
      >
        <span className="flex items-center gap-1.5">
          <kbd className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">
            D
          </kbd>
          {t('prejoin.hint_mic')}
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">
            V
          </kbd>
          {t('prejoin.hint_cam')}
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">
            Enter
          </kbd>
          {t('prejoin.hint_join')}
        </span>
      </div>
    </section>
  );
}

/**
 * The "Connection" check value: «Отличное · 8 мс»-style wording by RTT tier, or the
 * unreachable wording when the probe failed. Pending renders the mockup's "…" placeholder.
 */
function connectionCheckValue(
  rttMs: number | null | undefined,
  quality: ConnectionQuality,
  t: (key: string) => string,
): string {
  if (rttMs === undefined) {
    return '…';
  }
  if (rttMs === null) {
    return t('prejoin.quality_down');
  }
  return `${t(`prejoin.quality_${quality}`)} · ${rttMs} ${t('prejoin.ms')}`;
}

/**
 * One device-check row: pending spinner while the probe runs, emerald check + value once it
 * settles ("…" placeholder text keeps the row height stable either way).
 */
function CheckRow({
  testid,
  label,
  pending,
  value,
}: {
  readonly testid: string;
  readonly label: string;
  readonly pending: boolean;
  readonly value: string;
}): JSX.Element {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-2"
    >
      {pending ? (
        <span className="size-4 shrink-0 animate-pulse rounded-full border-2 border-zinc-700 border-t-amber-400" />
      ) : (
        <CircleCheck className="size-4 shrink-0 text-emerald-400" />
      )}
      <span className="text-xs font-medium text-zinc-300">{label}</span>
      <span className="ml-auto text-[11.5px] tabular-nums text-zinc-500">{value}</span>
    </div>
  );
}

/**
 * Speaker options: the browser's "default" output first (always selectable, even before
 * permission is granted), then the enumerated audiooutput devices with real labels.
 */
function speakerOptionsFrom(
  devices: readonly InputDeviceInfoLike[],
  defaultLabel: string,
): { deviceId: string; label: string }[] {
  const options: { deviceId: string; label: string }[] = [
    { deviceId: DEFAULT_SPEAKER_ID, label: defaultLabel },
  ];
  for (const option of deviceOptions(devices, 'audiooutput')) {
    if (option.deviceId !== DEFAULT_SPEAKER_ID) {
      options.push(option);
    }
  }
  return options;
}

function usernameForToken(accessToken: string): string {
  return decodeAccessTokenPayload(accessToken)?.preferredUsername ?? FALLBACK_USERNAME;
}

function videoOptionsLength(devices: readonly InputDeviceInfoLike[]): number {
  return deviceOptions(devices, 'videoinput').length;
}

function audioOptionsLength(devices: readonly InputDeviceInfoLike[]): number {
  return deviceOptions(devices, 'audioinput').length;
}
